import { inRange, requireEvidence, requireNumber, type CalculatorIssues } from "./calculator-input";

/** Exact 12/12-50A specifications, not Orion XS 70A or 1400.
 * Victron manual rev. 11 (06/2026), technical data §6.1, read 2026-09-11.
 * 700 W is nominal at 14 V, NOT a constant power cap (see table footnote 4).
 * https://www.victronenergy.com/media/pg/Orion_XS_12-12-50A_DC-DC_battery_charger/en/technical-data.html */
export const ORION_XS_50 = { inputMinV: 9, inputMaxV: 17, outputMinV: 10, outputMaxV: 17,
  inputMaxA: 50, outputMaxA: 50, maxEfficiencyPct: 98.5 } as const;

export type ChargerSizeInput = {
  requestedOutputA: number | null;
  /** Minimum loaded voltage AT the charger input, after cable loss. */
  inputVoltageV: number | null;
  /** Maximum intended charging voltage AT the charger output. */
  outputVoltageV: number | null;
  efficiencyPct: number | null;
  efficiencyEvidence: string;
  alternatorAvailableA: number | null;
  vehicleLoadsA: number | null;
  reserveA: number | null;
  vehicleEvidence: string;
  batteryMaxChargeA: number | null;
  bmsMaxChargeA: number | null;
  otherChargingA: number | null;
  batteryEvidence: string;
  bmsEvidence: string;
  /** Includes temperature, charge profile/voltage and working charge-disable control. */
  chargingPermitted: boolean | null;
  model: "unselected" | "orion-xs-50" | "documented";
  modelTemperatureC: number | null;
  modelCoolingConfirmed: boolean | null;
  modelEvidence: string;
  modelInputMinV: number | null;
  modelInputMaxV: number | null;
  modelOutputMinV: number | null;
  modelOutputMaxV: number | null;
  modelInputMaxA: number | null;
  /** Documented available output under the actual temperature/mounting conditions. */
  modelOutputMaxA: number | null;
};

export type ChargerSizeResult = CalculatorIssues & {
  status: "unknown" | "invalid" | "limited" | "ready";
  requestedOutputW: number | null;
  requiredInputW: number | null;
  requiredInputA: number | null;
  vehicleHeadroomA: number | null;
  vehicleOutputCeilingA: number | null;
  batteryOutputCeilingA: number | null;
  modelOutputCeilingA: number | null;
  /** A partial bound is not a fully checked charger recommendation. */
  knownOutputCeilingA: number | null;
  maximumOutputA: number | null;
  limiting: string[];
  conflicts: string[];
};

export function calculateChargerSize(input: ChargerSizeInput): ChargerSizeResult {
  const r: ChargerSizeResult = { status: "unknown", missing: [], invalid: [], unsupported: [], requestedOutputW: null,
    requiredInputW: null, requiredInputA: null, vehicleHeadroomA: null, vehicleOutputCeilingA: null,
    batteryOutputCeilingA: null, modelOutputCeilingA: null, knownOutputCeilingA: null, maximumOutputA: null,
    limiting: [], conflicts: [] };
  // Evaluate every field even when another is absent: callers see all missing evidence.
  const outputOk = requireNumber(r, "Requested output (A)", input.requestedOutputA, 0.1, 1000);
  const vinOk = requireNumber(r, "Minimum input voltage (V)", input.inputVoltageV, 1, 60);
  const voutOk = requireNumber(r, "Charging voltage (V)", input.outputVoltageV, 1, 60);
  const efficiencyOk = requireNumber(r, "Efficiency (%)", input.efficiencyPct, 1, 100);
  requireEvidence(r, "Efficiency basis", input.efficiencyEvidence);
  const altOk = requireNumber(r, "Available alternator output (A)", input.alternatorAvailableA, 0, 2000);
  const loadsOk = requireNumber(r, "Vehicle loads (A)", input.vehicleLoadsA, 0, 2000);
  const reserveOk = requireNumber(r, "Vehicle reserve (A)", input.reserveA, 0, 2000);
  const vehicleOk = requireEvidence(r, "Vehicle evidence at hot idle / worst operating point", input.vehicleEvidence);
  const batteryOk = requireNumber(r, "Battery charge limit (A)", input.batteryMaxChargeA, 0, 2000);
  const bmsOk = requireNumber(r, "BMS / charge-control limit (A)", input.bmsMaxChargeA, 0, 2000);
  const otherOk = requireNumber(r, "Other simultaneous charging (A)", input.otherChargingA, 0, 2000);
  const batteryEvidenceOk = requireEvidence(r, "Battery charge-limit evidence", input.batteryEvidence);
  const bmsEvidenceOk = requireEvidence(r, "BMS / charge-control evidence", input.bmsEvidence);
  if (input.chargingPermitted == null) r.missing.push("Battery temperature, voltage/profile and charge-disable controls");
  else if (input.chargingPermitted !== true && input.chargingPermitted !== false) r.invalid.push("Charging permission must be explicitly confirmed.");
  if (vinOk && voutOk && efficiencyOk) {
    if (outputOk) {
      r.requestedOutputW = input.requestedOutputA! * input.outputVoltageV!;
      r.requiredInputW = r.requestedOutputW / (input.efficiencyPct! / 100);
      r.requiredInputA = r.requiredInputW / input.inputVoltageV!;
    }
  }
  if (altOk && loadsOk && reserveOk && vehicleOk) {
    r.vehicleHeadroomA = Math.max(0, input.alternatorAvailableA! - input.vehicleLoadsA! - input.reserveA!);
    if (input.vehicleLoadsA! + input.reserveA! > input.alternatorAvailableA!) r.conflicts.push("Vehicle loads and reserve already exceed available alternator output.");
    if (vinOk && voutOk && efficiencyOk) r.vehicleOutputCeilingA = r.vehicleHeadroomA * input.inputVoltageV! * (input.efficiencyPct! / 100) / input.outputVoltageV!;
  }
  if (batteryOk && bmsOk && otherOk && batteryEvidenceOk && bmsEvidenceOk) {
    const limit = Math.min(input.batteryMaxChargeA!, input.bmsMaxChargeA!);
    r.batteryOutputCeilingA = Math.max(0, limit - input.otherChargingA!);
    if (input.otherChargingA! > limit) r.conflicts.push("Other charging already exceeds the battery or BMS limit.");
  }
  if (input.chargingPermitted === false) {
    r.batteryOutputCeilingA = 0;
    r.conflicts.push("Charging is not permitted under the stated battery conditions.");
  }
  let model: { inputMinV: number; inputMaxV: number; outputMinV: number; outputMaxV: number; inputMaxA: number; outputMaxA: number } | null = null;
  if (input.model === "orion-xs-50") {
    const tempOk = requireNumber(r, "Charger ambient temperature (°C)", input.modelTemperatureC, -20, 60);
    if (input.modelCoolingConfirmed !== true) r.missing.push("Orion mounting and cooling per manual");
    if (efficiencyOk && input.efficiencyPct! > ORION_XS_50.maxEfficiencyPct) r.invalid.push("Orion XS 12/12-50A efficiency cannot exceed its 98.5% published maximum.");
    if (tempOk && input.modelCoolingConfirmed === true) model = { ...ORION_XS_50,
      outputMaxA: ORION_XS_50.outputMaxA * (1 - Math.max(0, input.modelTemperatureC! - 40) * 0.015) };
    if (outputOk && input.requestedOutputA! < 1) r.invalid.push("Orion XS current settings start at 1 A.");
  } else if (input.model === "documented") {
    const modelChecks = [
      requireEvidence(r, "Exact model and operating-condition evidence", input.modelEvidence),
      requireNumber(r, "Model minimum input voltage (V)", input.modelInputMinV, 1, 60),
      requireNumber(r, "Model maximum input voltage (V)", input.modelInputMaxV, 1, 60),
      requireNumber(r, "Model minimum output voltage (V)", input.modelOutputMinV, 1, 60),
      requireNumber(r, "Model maximum output voltage (V)", input.modelOutputMaxV, 1, 60),
      requireNumber(r, "Model input limit (A)", input.modelInputMaxA, 0, 2000),
      requireNumber(r, "Model available output limit (A)", input.modelOutputMaxA, 0, 1000),
    ];
    if (modelChecks.every(Boolean)) model = { inputMinV: input.modelInputMinV!, inputMaxV: input.modelInputMaxV!,
      outputMinV: input.modelOutputMinV!, outputMaxV: input.modelOutputMaxV!, inputMaxA: input.modelInputMaxA!, outputMaxA: input.modelOutputMaxA! };
  } else if (input.model === "unselected") r.missing.push("Exact charger model limits");
  else r.invalid.push("Choose a supported model option.");
  if (model) {
    if (model.inputMinV > model.inputMaxV || model.outputMinV > model.outputMaxV) r.invalid.push("Model voltage ranges are reversed.");
    if (vinOk && !inRange(input.inputVoltageV, model.inputMinV, model.inputMaxV)) r.invalid.push("Input voltage is outside the selected model range.");
    if (voutOk && !inRange(input.outputVoltageV, model.outputMinV, model.outputMaxV)) r.invalid.push("Charging voltage is outside the selected model range.");
    if (vinOk && voutOk && efficiencyOk) r.modelOutputCeilingA = Math.min(model.outputMaxA,
      model.inputMaxA * input.inputVoltageV! * (input.efficiencyPct! / 100) / input.outputVoltageV!);
  }
  const ceilings = [ ["Vehicle headroom", r.vehicleOutputCeilingA], ["Battery / BMS", r.batteryOutputCeilingA], ["Charger model", r.modelOutputCeilingA] ] as const;
  const known = ceilings.filter((entry): entry is readonly [typeof entry[0], number] => entry[1] != null);
  if (known.length) {
    r.knownOutputCeilingA = Math.min(...known.map(([, value]) => value));
    r.limiting = known.filter(([, value]) => Math.abs(value - r.knownOutputCeilingA!) < 1e-8).map(([label]) => label);
  }
  if (r.invalid.length) r.status = "invalid";
  else if (r.missing.length === 0 && known.length === 3) {
    r.maximumOutputA = r.knownOutputCeilingA;
    r.status = input.requestedOutputA! <= r.maximumOutputA! + 1e-9 && !r.conflicts.length ? "ready" : "limited";
  }
  return r;
}

export const EMPTY_CHARGER_INPUT: ChargerSizeInput = { requestedOutputA: null, inputVoltageV: null, outputVoltageV: null,
  efficiencyPct: null, efficiencyEvidence: "", alternatorAvailableA: null, vehicleLoadsA: null, reserveA: null, vehicleEvidence: "",
  batteryMaxChargeA: null, bmsMaxChargeA: null, otherChargingA: null, batteryEvidence: "", bmsEvidence: "", chargingPermitted: null,
  model: "unselected", modelTemperatureC: null, modelCoolingConfirmed: null, modelEvidence: "", modelInputMinV: null, modelInputMaxV: null,
  modelOutputMinV: null, modelOutputMaxV: null, modelInputMaxA: null, modelOutputMaxA: null };

/** Deliberately synthetic evidence; never pre-filled on a real visitor's form. */
export const EXAMPLE_CHARGER_INPUT: ChargerSizeInput = { ...EMPTY_CHARGER_INPUT, requestedOutputA: 30, inputVoltageV: 12.5,
  outputVoltageV: 14.4, efficiencyPct: 90, efficiencyEvidence: "Illustrative 90% assumption; replace with evidence at this operating point.",
  alternatorAvailableA: 100, vehicleLoadsA: 40, reserveA: 10, vehicleEvidence: "Synthetic hot-idle example, not vehicle measurements.",
  batteryMaxChargeA: 60, bmsMaxChargeA: 50, otherChargingA: 10, batteryEvidence: "Synthetic battery limit, not a selected battery.",
  bmsEvidence: "Synthetic BMS limit, not a selected BMS.", chargingPermitted: true, model: "orion-xs-50", modelTemperatureC: 30, modelCoolingConfirmed: true };
